/**
 * 案件（Backlog Gen2 母票）の状態区分と、担当メンバーでの絞り込み — フロント/バック共有の単一 SoT。
 *
 * 「進行中か」は全体PJ看板（`ProjectsView`）・`GET /projects`・チーム週次MTG の自動ブロックが
 * 同じ判定を使う。画面と API に別々に書くと、片方だけ直したときに進行中PJ数がずれる
 * （実際に `ProjectsView.isActiveProject` と `get-projects-use-case.isActive` が二重定義だった）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の進行中・保留中・未対応未アサインの区分と担当メンバーでの絞り込みの単一 SoT
 * @graph-connects none
 */
/**
 * 保留中か。Backlog のステータスは「保留中」「保留中（〜）」のように揺れるため前方一致で見る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Tell whether a project status means on hold
 * @graph-connects none
 */
export function isOnHoldProjectStatus(status) {
    return status.startsWith('保留中');
}
/**
 * ステータス「未対応」かつ PL 未アサインか。完了でも保留でもないのに進行中から外れる唯一の形。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Tell whether a project is untouched and has no project leader assigned
 * @graph-connects none
 */
export function isPendingUnassignedProject(project) {
    return project.status === '未対応' && (project.assignee === null || project.assignee === '');
}
/**
 * 進行中か（完了・保留中・未対応未アサインを除く）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Tell whether a project is in progress (not completed, on hold, or untouched without a leader)
 * @graph-connects isOnHoldProjectStatus [calls] 保留中の判定
 * @graph-connects isPendingUnassignedProject [calls] 未対応未アサインの判定
 */
export function isActiveProject(project) {
    if (project.status === '完了')
        return false;
    if (isOnHoldProjectStatus(project.status))
        return false;
    return !isPendingUnassignedProject(project);
}
/**
 * 指定メンバーのいずれかが担当している案件 ID の集合。メールは大文字小文字を区別しない。
 * 全体PJ看板のチームタブと `GET /projects?team=` が同じ規則で絞るための関数。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Collect the project ids that any of the given members is assigned to
 * @graph-connects none
 */
export function projectIdsAssignedTo(assignments, memberEmails) {
    const emails = new Set([...memberEmails].map((email) => email.toLowerCase()));
    const ids = new Set();
    for (const assignment of assignments) {
        if (emails.has(assignment.memberEmail.toLowerCase()))
            ids.add(assignment.projectId);
    }
    return ids;
}
//# sourceMappingURL=project-status.js.map